import { compact, isEmpty, map, reverse, sortBy } from "lodash/fp";
import { DateTime } from "luxon";

import type { MomentInstanceType } from "@app/analytics/MeetingReport/components/Moments/types";
import type {
  CoachingDataWithoutTimestamps,
  CoachingModuleDataWithoutTimestamps,
} from "@app/analytics/MeetingReport/types";
import { scaleWpmColor } from "@app/analytics/utils";
import COACHING_BENCHMARKS from "@app/common/coaching/coachingBenchmarks";
import type {
  ApiChapter,
  ApiCoachingItem,
  ApiGptHighlight,
  ApiHighlight,
  ApiVisualEngagementHighlight,
  ApiVisualSentimentHighlight,
  BaseSessionChapter,
  BaseSessionHighlight,
  CoachingApiResponse,
} from "@app/common/types";
import { getSemanticScore, isWithinRange } from "@app/common/utils";
import {
  optionalParseUTCDateTimeFromString,
  parseUTCDateTimeFromString,
  titleize,
} from "@app/utils/utils";

const getDirectionFromDelta = (delta: number) =>
  delta < 0 ? "negative" : "positive";

const parseGptHighlight = (
  highlight: ApiGptHighlight
): BaseSessionHighlight => {
  const semanticScore = getSemanticScore(highlight.engagementScore);
  const label =
    semanticScore === "unknown"
      ? undefined
      : {
          metric: "engagement",
          impact: {
            value: titleize(semanticScore),
            direction: semanticScore,
          },
        };

  return {
    id: highlight.id,
    description: highlight.text,
    isDeleted: Boolean(highlight.isDeleted),
    isEdited: Boolean(highlight.isEdited),
    startTime: optionalParseUTCDateTimeFromString(highlight.startTime),
    endTime: optionalParseUTCDateTimeFromString(highlight.endTime),
    type: highlight.clipType,
    thumbnailUrl: highlight.thumbnailUrl,
    label,
  };
};

const parseVisualSentimentHighlight = (
  highlight: ApiVisualSentimentHighlight
): BaseSessionHighlight => {
  const direction = getDirectionFromDelta(highlight.visualSentimentDelta);

  return {
    id: highlight.id,
    description:
      direction === "positive"
        ? "Increase in Sentiment"
        : "Decrease in Sentiment",
    isDeleted: Boolean(highlight.isDeleted),
    isEdited: Boolean(highlight.isEdited),
    startTime: parseUTCDateTimeFromString(highlight.startTime),
    endTime: parseUTCDateTimeFromString(highlight.endTime),
    type: direction,
    quote: highlight.text,
    thumbnailUrl: highlight.thumbnailUrl,
    label: {
      metric: "sentiment",
      impact: {
        value: `${Math.abs(Math.round(highlight.visualSentimentDelta * 100))}%`,
        direction,
      },
    },
  };
};

const parseVisualEngagementHighlight = (
  highlight: ApiVisualEngagementHighlight
): BaseSessionHighlight => {
  const direction = getDirectionFromDelta(highlight.visualEngagementDelta);

  return {
    id: highlight.id,
    description:
      direction === "positive"
        ? "Increase in Engagement"
        : "Decrease in Engagement",
    isDeleted: Boolean(highlight.isDeleted),
    isEdited: Boolean(highlight.isEdited),
    startTime: parseUTCDateTimeFromString(highlight.startTime),
    endTime: parseUTCDateTimeFromString(highlight.endTime),
    type: direction,
    quote: highlight.text,
    thumbnailUrl: highlight.thumbnailUrl,
    label: {
      metric: "engagement",
      impact: {
        value: `${Math.abs(
          Math.round(highlight.visualEngagementDelta * 100)
        )}%`,
        direction,
      },
    },
  };
};

export const parseHighlights = (
  highlights: ApiHighlight[]
): BaseSessionHighlight[] => {
  if (isEmpty(highlights)) return [];

  const parsedHighlights = highlights.map((highlight) => {
    switch (highlight.clipType) {
      case "action_item":
      case "key_question":
      case "topic":
        return parseGptHighlight(highlight);
      case "visual_engagement":
        return parseVisualEngagementHighlight(highlight);
      case "visual_sentiment":
        return parseVisualSentimentHighlight(highlight);
      default:
        // not exposing language sentiment highlights right now
        return;
    }
  });

  return sortBy((h) => h.startTime?.toMillis(), compact(parsedHighlights));
};

export const parseChapters = (chapters: ApiChapter[]): BaseSessionChapter[] => {
  const parsedChapters = chapters.map((c) => ({
    id: c.id,
    title: c.text,
    isDeleted: Boolean(c.isDeleted),
    isEdited: Boolean(c.isEdited),
    description: c.secondaryText,
    startTime: parseUTCDateTimeFromString(c.startTime),
    endTime: parseUTCDateTimeFromString(c.endTime),
    thumbnailUrl: c.thumbnailUrl,
  }));

  return sortBy((c) => c.startTime.toMillis(), parsedChapters);
};

const DEFAULT_MODULE_DATA = {
  moments: [],
  rate: 0,
  totalCount: 0,
  words: [],
};

export const DEFAULT_COACHING_DATA: CoachingDataWithoutTimestamps = {
  fillerWords: DEFAULT_MODULE_DATA,
  talkingPace: DEFAULT_MODULE_DATA,
  interruptions: DEFAULT_MODULE_DATA,
  nonInclusiveTerms: DEFAULT_MODULE_DATA,
  questions: DEFAULT_MODULE_DATA,
};

const parseCoachingData = (
  data: ApiCoachingItem,
  instanceType: MomentInstanceType
): CoachingModuleDataWithoutTimestamps => {
  if (isEmpty(data)) return DEFAULT_MODULE_DATA;

  const moments = (data.turns ?? []).map((turn) => {
    const instances = Object.entries(turn.keyCounts ?? {}).map(
      ([word, count]) => ({
        label: data.keyMap?.[word] ?? word,
        count,
        type: instanceType,
      })
    );

    const startTime = parseUTCDateTimeFromString(
      turn.firstTime ?? turn.startTime
    );
    return { id: turn.turnId, instances, startTime };
  });

  const words = Object.entries(data.keyCounts ?? {}).map(([word, count]) => ({
    word,
    displayValue: data.keyMap?.[word] ?? word,
    count,
    turns: map("turnId", data.turns),
  }));

  const sortedMoments = sortBy((m) => m.startTime.toMillis(), moments);
  const sortedWords = reverse(sortBy("count", words));

  return {
    moments: sortedMoments,
    rate: (data.rate ?? 0) * 100,
    totalCount: data.count ?? 0,
    words: sortedWords,
  };
};

const parseTalkingPace = (
  data: ApiCoachingItem
): CoachingModuleDataWithoutTimestamps => {
  const moments = (data.turns ?? []).map((turn) => {
    const rate = turn.rate ?? 0;

    const isWithinRecommendedRange = isWithinRange(
      Math.round(rate),
      [COACHING_BENCHMARKS.wpm.min, COACHING_BENCHMARKS.wpm.max],
      { inclusiveEnd: true }
    );

    if (isWithinRecommendedRange) return;

    const startTime = DateTime.fromISO(turn.startTime, { zone: "utc" });
    const instance = {
      color: scaleWpmColor(rate),
      label: `${Math.round(rate)} wpm`,
    };

    return { id: turn.turnId, instances: [instance], startTime };
  });

  const sortedMoments = sortBy((m) => m.startTime.toMillis(), compact(moments));

  return {
    moments: sortedMoments,
    rate: Math.round(data.rate ?? 0),
    totalCount: 0, // irrelevant for wpm
    words: [], // irrelevant for qpm
  };
};

export const parseCoachingResponse = (
  json: CoachingApiResponse
): CoachingDataWithoutTimestamps => {
  const noData = isEmpty(json) || Object.values(json).every(isEmpty);
  if (noData) return DEFAULT_COACHING_DATA;

  const fillerWords = parseCoachingData(json.fillerWord, "negative");
  const interruptions = parseCoachingData(json.interruption, "neutral");
  const nonInclusiveTerms = parseCoachingData(
    json.nonInclusiveTerm,
    "negative"
  );
  const questions = parseCoachingData(json.question, "question");
  const talkingPace = parseTalkingPace(json.talkingPace);

  return {
    fillerWords,
    interruptions,
    nonInclusiveTerms,
    questions,
    talkingPace,
  };
};
